import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, MessageSquareText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { ADMIN_MESSAGE_MAX, RESOLVE_SUBMIT_LABELS } from '../constants/feedback.constants';
import { useResolveFeedback } from '../hooks/useResolveFeedback';
import {
  RESOLVE_STATUS,
  resolveFeedbackSchema,
  type FeedbackListItem,
  type ResolveFeedbackPayload,
  type ResolveStatus,
} from '../types/feedback.types';
import { FeedbackContextCard } from './FeedbackContextCard';
import { ResolveStatusSelector } from './ResolveStatusSelector';

interface ResolveFeedbackModalProps {
  feedback: FeedbackListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ResolveFeedbackModal({ feedback, open, onOpenChange }: ResolveFeedbackModalProps) {
  const form = useForm<ResolveFeedbackPayload>({
    resolver: zodResolver(resolveFeedbackSchema),
    defaultValues: {
      status: 'completed',
      adminMessage: '',
    },
  });

  const resolveMutation = useResolveFeedback(() => {
    form.reset();
    onOpenChange(false);
  });

  useEffect(() => {
    if (open && feedback) {
      const defaultStatus: ResolveStatus = RESOLVE_STATUS.includes(feedback.status as ResolveStatus)
        ? (feedback.status as ResolveStatus)
        : 'completed';
      form.reset({
        status: defaultStatus,
        adminMessage: feedback.adminMessage ?? '',
      });
    }
  }, [open, feedback, form]);

  function submit(values: ResolveFeedbackPayload) {
    if (!feedback) return;
    const trimmedMessage = values.adminMessage?.trim();
    const payload: ResolveFeedbackPayload = {
      status: values.status,
      ...(trimmedMessage ? { adminMessage: trimmedMessage } : {}),
    };
    resolveMutation.mutate({ id: feedback.id, payload });
  }

  if (!feedback) return null;

  const adminMessageLength = form.watch('adminMessage')?.length ?? 0;
  const selectedStatus = form.watch('status');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90dvh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="border-b p-4 pr-10 pb-3">
          <DialogTitle className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex size-7 items-center justify-center rounded-full',
                ACCENT_MARK_CLASS,
              )}
            >
              <MessageSquareText className="size-4" />
            </span>
            Feedback Details
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4">
              <FeedbackContextCard feedback={feedback} />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="gap-0.5">
                      Update Status<span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <ResolveStatusSelector value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="adminMessage"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Response</FormLabel>
                      <span
                        className={cn(
                          'text-2xs tabular-nums',
                          adminMessageLength > ADMIN_MESSAGE_MAX
                            ? 'text-destructive'
                            : 'text-muted-foreground',
                        )}
                      >
                        {adminMessageLength} / {ADMIN_MESSAGE_MAX}
                      </span>
                    </div>
                    <FormControl>
                      <textarea
                        {...field}
                        placeholder="Add a response visible to the user"
                        rows={4}
                        maxLength={ADMIN_MESSAGE_MAX}
                        className="min-h-24 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:border-ring focus:ring-1 focus:ring-ring/30 resize-y"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="mx-0 mb-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={resolveMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={resolveMutation.isPending} className="gap-1.5">
                {resolveMutation.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  RESOLVE_SUBMIT_LABELS[selectedStatus]
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
